import { Component, ComponentChildren } from 'preact'
import Categories from './categories'
import { ParentCategoryInfo } from '../mega-menu/Types'
import UnansweredTopics, { UnansweredTopicsProps } from './unanswered'
import RecentlyActiveTopics, { RecentlyActiveTopicsProps } from './recentlyActiveTopics'
import Phrase from '../../libs/translationsUtils/phrase'
import axios from 'axios'
import { z } from 'zod'
import WidgetContainer, { WidgetContainerConfig, widgetContainerConfig } from '../widget-container'
import classnames from 'classnames'

type TabsType = Array<{
  enabled: boolean
  collapsed: boolean
  phrase?: string | undefined
  phrase_suffix: 'categories' | 'unanswered' | 'recent_active'
  class: string
}>

interface Props extends WidgetContainerConfig {
  tabs: TabsType
  parentCategories: ParentCategoryInfo[]
  collapsed: null | {
    [key: number]: boolean
  }
  displayUnreadPostCount: boolean
  unAnsweredTopics: UnansweredTopicsProps
  recentlyActiveTopics: RecentlyActiveTopicsProps
}

interface State {
  tabs: TabsType
  activeTab: number
  unreadTopicsAndRepliesCount: null | {
    [key: string]: {
      topicCount?: number
      postCount?: number
    }
  }
}

export const widgetName = 'category_and_topics_stream_only'

export const config = z.object({
  tabs: z.array(z.object({
    enabled: z.boolean(),
    collapsed: z.boolean(),
    phrase: z.union([z.string(), z.undefined()]),
    phrase_suffix: z.enum(['categories', 'unanswered', 'recent_active']),
    class: z.string()
  }))
}).merge(widgetContainerConfig)

export type CategoryAndContentStreamConfig = z.infer<typeof config>

class CategoryAndTopicsStream extends Component<Props, State> {
  private readonly STORAGE_POSTFIX = 'tabs-helper-homepage'
  private readonly URLS = {
    categories: '#',
    unanswered: '/search/activity/unanswered',
    recent_active: '/search/activity/recent'
  }

  constructor (props: Props) {
    super(props)
    const enabledTabs = props.tabs.filter(tab => tab.enabled)
    const setActiveTab = typeof window === 'undefined' ? 0 : parseInt(window.localStorage.getItem(this.STORAGE_POSTFIX) ?? '1') - 1
    this.state = {
      tabs: enabledTabs,
      activeTab: setActiveTab >= enabledTabs.length ? 0 : setActiveTab,
      unreadTopicsAndRepliesCount: null
    }
    this.handleChange = this.handleChange.bind(this)
  }

  handleChange (idx: number) {
    this.setState({ activeTab: idx }, () => {
      if (typeof window === 'undefined') return

      window.localStorage.setItem(this.STORAGE_POSTFIX, (this.state.activeTab + 1).toString())
    })
  }

  async componentDidMount () {
    const topicIds: string[] = this.props.parentCategories.reduce((newList: string[], parentCategory) => (
      newList.concat(parentCategory.subforums.map(category => category.id.toString()))
    ), [])

    if (this.props.displayUnreadPostCount) {
      try {
        const { data } = await axios.get('/topic/unreadPostDataInReadableForums', {
          params: {
            topicIds
          }
        })
        this.setState({ unreadTopicsAndRepliesCount: data })
      } catch (e) {
        this.setState({ unreadTopicsAndRepliesCount: null })
      }
    }
  }

  render () {
    const { title, subtitle, backgroundColor, backgroundColorDisplay } = this.props
    const { tabs, activeTab } = this.state
    const categoriesTabIdx = tabs.findIndex(tab => tab.phrase_suffix === 'categories')

    const COMPONENTS: {
      [key: string]: ComponentChildren
    } = {
      categories: <Categories
        parentCategories={this.props.parentCategories}
        collapsed={this.props.collapsed ?? {}}
        displayUnreadPostCount={this.props.displayUnreadPostCount}
        unreadTopicsAndRepliesCount={this.state.unreadTopicsAndRepliesCount}
        isDefaultCollapsed={tabs[categoriesTabIdx]?.collapsed || false}
                  />,
      unanswered: <UnansweredTopics {...this.props.unAnsweredTopics} />,
      recent_active: <RecentlyActiveTopics {...this.props.recentlyActiveTopics} />
    }

    return (
      <WidgetContainer
        config={{
          title,
          subtitle,
          backgroundColor,
          backgroundColorDisplay
        }}
        widgetName='category-and-topic-stream'
      >
        <div className={classnames('box', 'pagebox', 'box--unbox')}>
          <div className='box__content'>
            <div className={classnames('module', 'widget--tabs', 'tabs', 'qa-widget-tabs')}>
              <nav className='sections__nav sections__nav--accordion-S'>
                <div className='tabs--nav__container'>
                  <div className='tabs--nav'>
                    <ul className='tabs--navigation qa-tabs-menu'>
                      {
                        tabs.map((tab, idx) => (
                          <li key={tab.phrase_suffix} className={classnames('tabs__item', 'tabs__item--M', 'qa-tab')} onClick={() => this.handleChange(idx)}>
                            <a
                              href={this.URLS[tab.phrase_suffix]}
                              onClick={(e) => e.preventDefault()}
                              className={`qa-link-tab js-tab ${tab.class}${activeTab === idx ? ' is-active' : ''}`}
                            >{tab.phrase || <Phrase p={`tab.navigation.${(tab.phrase_suffix || '').replace(' ', '_')}`} />}</a>
                          </li>
                        ))
                      }
                    </ul>
                  </div>
                </div>
                <div className='tabs--navigation'>
                  {
                    tabs.map((tab, idx) => (
                      <div className='tabs qa-tabs' key={tab.phrase}>
                        <div className={`js-content js-content-1 qa-tab-isVisible js-content-${idx + 1}${activeTab !== idx ? ' is-hidden--loose' : ''}`}>{COMPONENTS[tab.phrase_suffix]}</div>
                      </div>
                    ))
                  }
                  <div className='tabs qa-tabs' />
                </div>
              </nav>
            </div>
          </div>
        </div>
      </WidgetContainer>
    )
  }
}

export default CategoryAndTopicsStream
